import React, { Suspense } from "react";

import AttachmentThumbs from "@/components/attachment-thumbs";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { GroupLinkPreview } from "@/features/groups/components/group-link-preview";
import StatusCard from "@/features/status/components/status-card";
import {
  MediaGallery,
  Audio,
  StatusVideoPlayer,
  LiveVideoPlayer,
} from "@/features/ui/util/async-components";
import { getActualStatus } from "@/utils/status";

import type { Status, Attachment } from "@/schemas";

interface StatusMediaProps {
  /** Status entity to render media for. */
  status: Status;
  /** Whether to display compact media. */
  muted?: boolean;
  /** Callback when compact media is clicked. */
  onClick?: () => void;
  /** Whether or not the media is concealed behind a NSFW banner. */
  showMedia?: boolean;
  /** Callback when visibility is toggled (eg clicked through NSFW). */
  onToggleVisibility?: () => void;
}

/** Render media attachments for a status. */
const StatusMedia: React.FC<StatusMediaProps> = ({
  status,
  muted = false,
  onClick,
  showMedia = true,
  onToggleVisibility = () => {},
}) => {
  const { currentAccount } = useAuth();
  const shouldAutoPlayVideo = currentAccount?.settings?.autoPlayVideo;
  const actualStatus = getActualStatus(status);
  const { openModal, closeModal } = useModalContext();

  const size = status.media_attachments.length;
  const firstAttachment = status.media_attachments[0];

  let media: React.JSX.Element | null = null;

  const renderLoadingMediaGallery = () => {
    return <div style={{ height: "285px" }} />;
  };

  const renderLoadingVideoPlayer = () => {
    return (
      <div
        className="relative mt-2 block cursor-pointer border-0 bg-cover bg-center bg-no-repeat"
        style={{ height: "285px" }}
      />
    );
  };

  const renderLoadingAudioPlayer = () => {
    return (
      <div
        className="relative mt-2 block cursor-pointer border-0 bg-cover bg-center bg-no-repeat"
        style={{ height: "285px" }}
      />
    );
  };

  const openMedia = (attachments: Attachment[], index: number) => {
    closeModal();
    openModal("MEDIA", {
      media: attachments.map((attachment) => ({
        attachment,
        statusId: status.id,
      })),
      index,
    });
  };

  if (size > 0 && firstAttachment) {
    if (muted) {
      media = (
        <AttachmentThumbs
          media={status.media_attachments}
          onClick={onClick}
          sensitive={status.sensitive}
        />
      );
    } else if (size === 1 && firstAttachment.type === "video") {
      const video = firstAttachment;

      media = (
        <div className="flex flex-col gap-2">
          <Suspense fallback={renderLoadingVideoPlayer()}>
            <StatusVideoPlayer
              attachment={video}
              shouldAutoPlayVideo={shouldAutoPlayVideo}
              isSensitive={!showMedia}
              className="rounded-2xl"
            />
          </Suspense>

          {status.sponsored && status.card && (
            <StatusCard card={status.card} horizontal />
          )}
        </div>
      );
    } else if (size === 1 && firstAttachment.type === "tv") {
      media = (
        <Suspense fallback={renderLoadingVideoPlayer()}>
          <LiveVideoPlayer
            key={status.id}
            status={status}
            className="rounded-2xl"
          />
        </Suspense>
      );
    } else if (size === 1 && firstAttachment.type === "audio") {
      const attachment = firstAttachment;

      media = (
        <Suspense fallback={renderLoadingAudioPlayer()}>
          <Audio
            src={attachment.url}
            alt={attachment.description}
            poster={
              attachment.preview_url !== attachment.url
                ? attachment.preview_url
                : status.account?.avatar_static
            }
            backgroundColor={attachment.meta?.colors?.background}
            foregroundColor={attachment.meta?.colors?.foreground}
            accentColor={attachment.meta?.colors?.accent}
            duration={attachment.meta?.original?.duration || 0}
            height={263}
          />
        </Suspense>
      );
    } else {
      media = (
        <Suspense fallback={renderLoadingMediaGallery()}>
          <MediaGallery
            media={status.media_attachments}
            sensitive={status.sensitive}
            height={285}
            onOpenMedia={openMedia}
            visible={showMedia}
            onToggleVisibility={onToggleVisibility}
          />
        </Suspense>
      );
    }
  } else if (!status.quote && status.card?.group) {
    media = <GroupLinkPreview card={status.card} />;
  } else if (!status.quote && status.card) {
    media = (
      <StatusCard
        card={status.card}
        horizontal={actualStatus.sponsored || undefined}
      />
    );
  }

  if (media) {
    return <div onClick={(e) => e.stopPropagation()}>{media}</div>;
  } else {
    return null;
  }
};

export default StatusMedia;
